const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    /** Marshmallow/smorest 422 `errors` payload — parsed by lib/kbFieldErrors. */
    public errors?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type FetchOptions = RequestInit & { skipAuth?: boolean };

/**
 * Run a ``fetch`` call, translating its raw network failure into a structured
 * ``ApiError``. The browser raises ``TypeError: Failed to fetch`` when the
 * request never reaches a server (offline, DNS, CORS, aborted) — letting that
 * bubble would leak the English exception to the UI (F093/A04). We re-throw as
 * ``ApiError(0, '', 'network_error')`` so callers map it through
 * ``apiErrorMessage`` to a friendly pt string. Status ``0`` = "no response".
 */
async function wrapNetworkErrors(run: () => Promise<Response>): Promise<Response> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(0, '', 'network_error');
  }
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { skipAuth, ...rest } = options;
  const token = skipAuth ? null : localStorage.getItem('jwt');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLanguage(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...rest.headers,
  };

  const response = await wrapNetworkErrors(() => fetch(`${API_BASE}${path}`, { ...rest, headers }));

  if (response.status === 401 && !skipAuth) {
    localStorage.removeItem('jwt');
    window.dispatchEvent(new Event('auth:unauthorized'));
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}) as Record<string, unknown>);
    const err = (data as { error?: { message?: string; code?: string } }).error;
    const fieldErrors = (data as { errors?: unknown }).errors;
    throw new ApiError(
      response.status,
      err?.message ?? response.statusText,
      err?.code,
      fieldErrors
    );
  }

  // 204 has no body; response.json() would throw SyntaxError on the empty
  // payload. Return undefined cast as T so DELETE-style endpoints can declare
  // ``Promise<void>`` cleanly without ad-hoc catches in every caller.
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// apiFetchBlob — reusable authenticated binary download primitive.
// Shares the same auth conventions as apiFetch (JWT, Accept-Language, 401
// side-effects) but skips Content-Type:application/json and returns a Blob
// instead of JSON. Intended for CSV/PDF exports — future callers: F042
// conversations export, billing statements, etc.
// ---------------------------------------------------------------------------
export interface BlobResult {
  blob: Blob;
  filename: string;
}

export async function apiFetchBlob(
  path: string,
  options: Omit<FetchOptions, 'skipAuth'> = {}
): Promise<BlobResult> {
  const token = localStorage.getItem('jwt');

  // Intentionally a plain object so callers can read `init.headers.Authorization`
  // directly (e.g. in tests) without going through the Headers API.
  const headers: Record<string, string> = {
    'Accept-Language': getCurrentLanguage(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  const response = await wrapNetworkErrors(() =>
    fetch(`${API_BASE}${path}`, { ...options, headers })
  );

  if (response.status === 401) {
    localStorage.removeItem('jwt');
    window.dispatchEvent(new Event('auth:unauthorized'));
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}) as Record<string, unknown>);
    const err = (data as { error?: { message?: string; code?: string } }).error;
    throw new ApiError(response.status, err?.message ?? response.statusText, err?.code);
  }

  const blob = await response.blob();

  // Parse filename from Content-Disposition: attachment; filename="leads.csv"
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'export.csv';

  return { blob, filename };
}

function getCurrentLanguage(): string {
  return localStorage.getItem('i18nextLng') || 'pt-BR';
}
